import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { colors, layout } from '../NewHire/v2/styles';
import AnnualReviewService from '../../../services/AnnualReview';
import WelcomeScreen from './WelcomeScreen';
import RatingSectionStep from './steps/RatingSectionStep';
import SelfReflectionStep from './steps/SelfReflectionStep';
import {
  AnnualReviewEmployeeAnswers,
  AnnualReviewResponse,
  AnnualReviewStepDescriptor,
  EMPLOYEE_STEPS,
  resumeStepIndex,
} from './types';
import { ANNUAL_REVIEW_RATINGS, CORE_VALUES_SUBITEMS } from './utils/questionsCatalog';

const ALL_RATING_KEYS = [...ANNUAL_REVIEW_RATINGS.map((q) => q.key), ...CORE_VALUES_SUBITEMS.map((s) => s.key)];

const computeCompletedKeys = (answers: AnnualReviewEmployeeAnswers | null | undefined): Set<string> => {
  const done = new Set<string>();
  if (!answers) return done;
  const ratings = answers.ratings || {};
  ([1, 2, 3, 4] as const).forEach((section) => {
    const items = ANNUAL_REVIEW_RATINGS.filter((q) => q.section === section);
    const allRated = items.every((q) => typeof ratings[q.key] === 'number');
    const coreOk = section !== 4 || CORE_VALUES_SUBITEMS.every((s) => typeof ratings[s.key] === 'number');
    if (allRated && coreOk) {
      done.add(`section_${section}`);
    }
  });
  const reflections = answers.selfReflection || {};
  const reflectionCount = Object.values(reflections).filter((v) => v?.trim()).length;
  if (reflectionCount >= 1) done.add('self_reflection');
  return done;
};

const AnnualReviewFlow = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [review, setReview] = useState<AnnualReviewResponse | null>(null);
  const [phase, setPhase] = useState<'welcome' | 'steps' | 'done'>('welcome');
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [stepIndex, phase]);
  const [answers, setAnswers] = useState<AnnualReviewEmployeeAnswers>({
    ratings: {},
    examples: {},
    selfReflection: {},
  });

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        const res = await AnnualReviewService('').getByToken(token);
        const data: AnnualReviewResponse = res.data;
        setReview(data);
        if (data.employeeAnswers) {
          setAnswers({
            ratings: data.employeeAnswers.ratings || {},
            examples: data.employeeAnswers.examples || {},
            selfReflection: data.employeeAnswers.selfReflection || {},
          });
        }
        if (data.status === 'FINISHED' || data.employeeCompletedAt) setPhase('done');
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Could not load annual review');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const completedKeys = useMemo(() => computeCompletedKeys(answers), [answers]);

  const steps: AnnualReviewStepDescriptor[] = EMPLOYEE_STEPS;

  const persistDraft = async (nextAnswers: AnnualReviewEmployeeAnswers) => {
    if (!token) return;
    try {
      await AnnualReviewService('').submitEmployeeAnswers(token, { answers: nextAnswers, isDraft: true });
    } catch (e) {
      // soft-fail draft saves
    }
  };

  const handleRatingChange = (key: string, value: number) => {
    const ratings = { ...(answers.ratings || {}), [key]: value };
    const next = { ...answers, ratings };
    setAnswers(next);
    persistDraft(next);
  };

  const handleExamplesChange = (sectionKey: string, value: string) => {
    const examples = { ...(answers.examples || {}), [sectionKey]: value };
    const next = { ...answers, examples };
    setAnswers(next);
    persistDraft(next);
  };

  const handleReflectionChange = (key: string, value: string) => {
    const selfReflection = { ...(answers.selfReflection || {}), [key]: value };
    const next = { ...answers, selfReflection };
    setAnswers(next);
    persistDraft(next);
  };

  const canSubmit = useMemo(() => {
    return ALL_RATING_KEYS.every((k) => typeof (answers.ratings || {})[k] === 'number');
  }, [answers]);

  const submitFinal = async () => {
    if (!token) return;
    if (!canSubmit) {
      toast.error('Please rate every category before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await AnnualReviewService('').submitEmployeeAnswers(token, { answers, isDraft: false });
      setPhase('done');
      toast.success('Annual review submitted. Thank you!');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not submit annual review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ ...layout.page, textAlign: 'center', paddingTop: 80 }}>
        <p style={{ color: colors.muted }}>Loading…</p>
      </div>
    );
  }

  if (!review) {
    return (
      <div style={{ ...layout.page, textAlign: 'center', paddingTop: 80 }}>
        <p style={{ color: colors.danger }}>Annual review not found.</p>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div style={layout.page}>
        <div style={{ ...layout.container, maxWidth: 640 }}>
          <div style={{ ...layout.card, textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ fontSize: 48, color: colors.success, marginBottom: 12 }}>
              <i className="fas fa-check-circle" />
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Annual review submitted</h1>
            <p style={{ color: colors.muted, marginTop: 10 }}>
              Thank you for completing your annual review. Your supervisor will be notified when their portion is ready
              and you will receive the final report once approved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'welcome') {
    return (
      <WelcomeScreen
        steps={steps}
        completedKeys={completedKeys}
        userName={review.employeeName}
        cycleYear={review.cycleYear}
        onStart={() => {
          setStepIndex(resumeStepIndex(steps, completedKeys));
          setPhase('steps');
        }}
      />
    );
  }

  const currentStep = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const isReview = currentStep.key === 'review_submit';

  return (
    <div style={layout.page}>
      <div style={layout.container}>
        <div style={layout.card}>
          <div style={{ fontSize: 13, color: colors.muted, marginBottom: 6 }}>
            Step {stepIndex + 1} of {steps.length}
          </div>
          {currentStep.key === 'section_1' ||
          currentStep.key === 'section_2' ||
          currentStep.key === 'section_3' ||
          currentStep.key === 'section_4' ? (
            <RatingSectionStep
              section={Number(currentStep.key.split('_')[1]) as 1 | 2 | 3 | 4}
              ratings={answers.ratings || {}}
              examples={answers.examples || {}}
              onRatingChange={handleRatingChange}
              onExamplesChange={handleExamplesChange}
            />
          ) : null}
          {currentStep.key === 'self_reflection' ? (
            <SelfReflectionStep values={answers.selfReflection || {}} onChange={handleReflectionChange} />
          ) : null}
          {isReview ? (
            <div>
              <h2 style={{ ...layout.title, marginBottom: 4 }}>Review & Submit</h2>
              <p style={layout.subtitle as any}>
                Confirm that you have completed every section. Once submitted, your answers will be shared with your
                supervisor.
              </p>
              <ul style={{ marginTop: 16, paddingLeft: 18 }}>
                {steps
                  .filter((s) => s.key !== 'review_submit')
                  .map((s) => (
                    <li key={s.key} style={{ marginBottom: 8 }}>
                      <strong>{s.title}</strong> — {completedKeys.has(s.key) ? 'completed' : 'pending'}
                    </li>
                  ))}
              </ul>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'space-between' }}>
            <button
              type="button"
              onClick={() => (stepIndex === 0 ? setPhase('welcome') : setStepIndex(stepIndex - 1))}
              style={layout.secondaryBtn}
            >
              <i className="fas fa-arrow-left" /> Back
            </button>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" onClick={() => setPhase('welcome')} style={layout.secondaryBtn}>
                Save & Continue Later
              </button>
              {isLast ? (
                <button
                  type="button"
                  onClick={submitFinal}
                  disabled={submitting || !canSubmit}
                  style={canSubmit && !submitting ? layout.primaryBtn : layout.disabledBtn}
                >
                  {submitting ? 'Submitting…' : 'Submit Annual Review'}
                </button>
              ) : (
                <button type="button" onClick={() => setStepIndex(stepIndex + 1)} style={layout.primaryBtn}>
                  Next <i className="fas fa-arrow-right" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnualReviewFlow;
